import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import {
  getIntelligenceDocuments,
  getIntelligenceDocumentDetail,
  type DocumentRow,
  type DocumentDetailResponse,
  type DocumentsParams,
} from '../lib/api';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse, shouldRetryApiError } from '../lib/api-errors';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function statusBadge(status: string | null, type: 'fetch' | 'extract'): React.ReactElement {
  const colors: Record<string, string> = {
    fetched: 'bg-green-100 text-green-800',
    extracted: 'bg-green-100 text-green-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-700',
    partial: 'bg-yellow-100 text-yellow-800',
    skipped: 'bg-slate-100 text-slate-600',
  };
  const label = status ?? '—';
  const colorClass = status ? (colors[status] ?? 'bg-slate-100 text-slate-600') : 'text-slate-400';
  void type;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
}

function confidencePill(score: number | null): React.ReactElement {
  if (score === null) return <span className="text-slate-400 text-xs">—</span>;
  const pct = Math.round(score * 100);
  const color = pct >= 80 ? 'text-green-700' : pct >= 60 ? 'text-amber-700' : 'text-red-700';
  return <span className={`text-xs font-medium tabular-nums ${color}`}>{pct}%</span>;
}

// ── Document Detail Panel ─────────────────────────────────────────────────────

interface DetailPanelProps {
  readonly documentId: string;
  readonly token: string;
  readonly onClose: () => void;
}

function DocumentDetailPanel({ documentId, token, onClose }: DetailPanelProps): React.ReactElement {
  const { data, error } = useSWR<DocumentDetailResponse>(
    ['intelligence-document-detail', documentId],
    async () => {
      const res = await getIntelligenceDocumentDetail(token, documentId);
      const body = (await res.json().catch(() => null)) as DocumentDetailResponse | { error: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load document detail.');
      }
      return body as DocumentDetailResponse;
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const doc = data?.document;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" role="dialog" aria-modal="true" aria-label="Document detail">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 truncate pr-4">
            {doc ? (doc.title ?? doc.company_name_raw ?? 'Document Detail') : 'Loading…'}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-xl leading-none ml-2"
            aria-label="Close detail panel"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {error ? (
            <ApiErrorNotice error={error} fallbackMessage="Failed to load document." />
          ) : !data ? (
            <div className="text-slate-500 text-sm">Loading document…</div>
          ) : (
            <>
              {/* Source metadata */}
              <section className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  Source Metadata
                </h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-slate-500">Agency</dt>
                  <dd className="text-slate-800">{doc?.agency ?? '—'}</dd>

                  <dt className="text-slate-500">Document type</dt>
                  <dd className="text-slate-800">{doc?.document_type ?? '—'}</dd>

                  <dt className="text-slate-500">Issuing office</dt>
                  <dd className="text-slate-800">{doc?.issuing_office ?? '—'}</dd>

                  <dt className="text-slate-500">Issue date</dt>
                  <dd className="text-slate-800">{doc?.issue_date ?? '—'}</dd>

                  <dt className="text-slate-500">Posted date</dt>
                  <dd className="text-slate-800">{doc?.posted_date ?? '—'}</dd>

                  <dt className="text-slate-500">Company</dt>
                  <dd className="text-slate-800">{doc?.company_name_raw ?? '—'}</dd>

                  <dt className="text-slate-500">FEI number</dt>
                  <dd className="text-slate-800 font-mono text-xs">{doc?.fei_number ?? '—'}</dd>

                  <dt className="text-slate-500">Fetch status</dt>
                  <dd>{statusBadge(doc?.fetch_status ?? null, 'fetch')}</dd>

                  <dt className="text-slate-500">Extract status</dt>
                  <dd>{statusBadge(doc?.extract_status ?? null, 'extract')}</dd>

                  {doc?.canonical_url && (
                    <>
                      <dt className="text-slate-500">Source URL</dt>
                      <dd>
                        <a
                          href={doc.canonical_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sentinel-700 hover:underline text-xs break-all"
                          data-testid="doc-canonical-url"
                        >
                          {doc.canonical_url}
                        </a>
                      </dd>
                    </>
                  )}

                  {doc?.current_asset_sha256 && (
                    <>
                      <dt className="text-slate-500">Asset SHA-256</dt>
                      <dd className="text-slate-600 font-mono text-xs break-all">
                        {doc.current_asset_sha256}
                      </dd>
                    </>
                  )}
                </dl>
              </section>

              {/* R2 artifacts */}
              {data.assets.length > 0 && (
                <section className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                    R2 Artifacts
                  </h4>
                  <ul className="space-y-1">
                    {data.assets.map((a) => (
                      <li key={a.asset_id} className="text-xs text-slate-600 font-mono bg-slate-50 rounded px-3 py-1.5">
                        {String(a.r2_key ?? '—')}
                        <span className="ml-2 text-slate-400 font-sans">{String(a.mime_type ?? '')}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-400 mt-1">R2 keys shown; signed URLs not available in v1.</p>
                </section>
              )}

              {/* Extracted findings */}
              <section className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  Extracted Findings ({data.findings.length})
                </h4>
                {data.findings.length === 0 ? (
                  <p className="text-sm text-slate-400">No findings extracted yet.</p>
                ) : (
                  <ul className="space-y-4" data-testid="findings-list">
                    {data.findings.map((f, i) => (
                      <li key={i} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {f.finding_number && (
                            <span className="text-xs font-mono text-slate-500">{String(f.finding_number)}</span>
                          )}
                          {f.gmp_system && (
                            <span className="rounded-full bg-blue-50 text-blue-700 text-xs px-2 py-0.5">{String(f.gmp_system)}</span>
                          )}
                          {f.severity && (
                            <span className="rounded-full bg-slate-100 text-slate-600 text-xs px-2 py-0.5">{String(f.severity)}</span>
                          )}
                          <span className="ml-auto">{confidencePill(typeof f.confidence_score === 'number' ? f.confidence_score : null)}</span>
                        </div>
                        <p className="text-sm text-slate-800 mb-2">{String(f.finding_text ?? '—')}</p>
                        {f.evidence_excerpt && (
                          <blockquote className="border-l-2 border-blue-200 pl-3 text-xs text-slate-500 italic">
                            {String(f.evidence_excerpt)}
                          </blockquote>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Citations */}
              {data.citations.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                    Citations ({data.citations.length})
                  </h4>
                  <ul className="space-y-2" data-testid="citations-list">
                    {data.citations.map((c) => (
                      <li key={c.citation_id} className="text-sm text-slate-700 flex items-start gap-2">
                        {c.citation_type && (
                          <span className="text-xs font-mono text-slate-500 mt-0.5 whitespace-nowrap">
                            {String(c.citation_type)}
                          </span>
                        )}
                        <span>{String(c.citation_text ?? '—')}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Documents Page ─────────────────────────────────────────────────────────────

export function IntelligenceDocumentsPage(): React.ReactElement {
  const { user, session } = useAuth();

  const [query, setQuery] = useState('');
  const [agency, setAgency] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  const LIMIT = 20;

  const params: DocumentsParams = {
    ...(query.trim() ? { query: query.trim() } : {}),
    ...(agency.trim() ? { agency: agency.trim() } : {}),
    ...(dateFrom && DATE_REGEX.test(dateFrom) ? { date_from: dateFrom } : {}),
    ...(dateTo && DATE_REGEX.test(dateTo) ? { date_to: dateTo } : {}),
    limit: LIMIT,
    offset: page * LIMIT,
  };

  const cacheKey = user && session
    ? ['intelligence-documents', user.id, JSON.stringify(params)]
    : null;

  const { data, error } = useSWR<{ documents: DocumentRow[]; meta: { total: number | null } }>(
    cacheKey,
    async () => {
      const res = await getIntelligenceDocuments(session!.access_token, params);
      const body = (await res.json().catch(() => null)) as
        | { documents: DocumentRow[]; meta: { total: number | null } }
        | { error: string }
        | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load documents.');
      }
      return body as { documents: DocumentRow[]; meta: { total: number | null } };
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const docs = data?.documents ?? [];
  const total = data?.meta?.total ?? null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Documents</h2>
        <Link
          to="/intelligence/similarity"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          Similarity Workbench →
        </Link>
      </div>

      <p className="text-sm text-slate-500 mb-5">
        Search and filter ingested regulatory source documents. Click a row to view extracted
        findings, citations, and artifact details.
      </p>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="doc-query">
              Search
            </label>
            <input
              id="doc-query"
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); }}
              placeholder="Company name or title…"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="doc-agency">
              Agency
            </label>
            <input
              id="doc-agency"
              type="text"
              value={agency}
              onChange={(e) => { setAgency(e.target.value); setPage(0); }}
              placeholder="e.g. FDA"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="doc-date-from">
              Posted from
            </label>
            <input
              id="doc-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>
        </div>
      </div>

      {error ? (
        <ApiErrorNotice error={error} fallbackMessage="Failed to load documents." />
      ) : !data ? (
        <div className="text-slate-500 text-sm">Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-slate-400 text-sm">
          No documents found. Try widening your search or removing filters.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Title / Company</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Agency</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Type</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Posted</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Fetch</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Extract</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => (
                  <tr
                    key={doc.source_document_id}
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedDocId(doc.source_document_id)}
                  >
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-800 truncate max-w-xs">
                        {doc.title ?? doc.company_name_raw ?? '—'}
                      </p>
                      {doc.title && doc.company_name_raw && (
                        <p className="text-xs text-slate-500 truncate">{doc.company_name_raw}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{doc.agency ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{doc.document_type ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500">{doc.posted_date ?? '—'}</td>
                    <td className="px-4 py-2.5">{statusBadge(doc.fetch_status ?? null, 'fetch')}</td>
                    <td className="px-4 py-2.5">{statusBadge(doc.extract_status ?? null, 'extract')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>
              {total !== null
                ? `${page * LIMIT + 1}–${Math.min((page + 1) * LIMIT, total)} of ${total}`
                : `Page ${page + 1}`}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={docs.length < LIMIT}
                className="px-3 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Detail side panel */}
      {selectedDocId && session && (
        <DocumentDetailPanel
          documentId={selectedDocId}
          token={session.access_token}
          onClose={() => setSelectedDocId(null)}
        />
      )}
    </div>
  );
}
